<script setup lang="ts">
/**
 * FilterConditionRow
 *
 * Single filter condition row within the advanced builder.
 * Renders field, operator, and value inputs adapted by field type.
 * Uses the generic getFieldValues() endpoint for searchable dropdowns.
 */

import { computed, watch, ref, onMounted } from 'vue'
import type { FilterCondition, SchemaField, FilterOperator, SavedViewEntityType } from '../api/types'
import { DYNAMIC_TOKENS } from '../api/types'
import { savedViewService } from '../api/service'
import MultiSelectDropdown from '@/components/MultiSelectDropdown.vue'
import type { MultiSelectOption } from '@/components/MultiSelectDropdown.vue'

interface Props {
    condition: FilterCondition
    fields: SchemaField[]
    entityType: SavedViewEntityType
    removable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    removable: true,
})

const emit = defineEmits<{
    update: [updates: Partial<FilterCondition>]
    remove: []
}>()

/** Currently selected field schema */
const selectedField = computed((): SchemaField | undefined =>
    props.fields.find(f => f.name === props.condition.field)
)

/** Operators available for the selected field */
const availableOperators = computed((): FilterOperator[] =>
    selectedField.value?.operators ?? ['$eq', '$ne', '$contains']
)

/** Whether the value input should be a date */
const isDate = computed(() => selectedField.value?.type === 'date')

/** Label for an operator */
function operatorLabel(op: FilterOperator): string {
    if (isDate.value) {
        const dateLabels: Record<string, string> = {
            $eq: 'is',
            $ne: 'is not',
            $gt: 'is after',
            $gte: 'is on or after',
            $lt: 'is before',
            $lte: 'is on or before',
            $in: 'is any of',
            $nin: 'is none of',
            $exists: 'exists',
        }
        return dateLabels[op] || op
    }

    const labels: Record<string, string> = {
        $eq: 'equals',
        $ne: 'not equals',
        $gt: 'greater than',
        $gte: 'at least',
        $lt: 'less than',
        $lte: 'at most',
        $in: 'is any of',
        $nin: 'is none of',
        $contains: 'contains',
        $regex: 'contains',
        $startsWith: 'starts with',
        $endsWith: 'ends with',
        $exists: 'exists',
    }
    return labels[op] || op
}

/** Type icon for a field */
function fieldIcon(type: string): string {
    const icons: Record<string, string> = {
        string: 'Aa',
        number: '#',
        boolean: '⊘',
        'virtual-boolean': '⊘',
        enum: '≡',
        date: '📅',
        relation: '🔗',
    }
    return icons[type] || '•'
}

/** Whether the value input should be a select (enum) */
const isEnum = computed(() => selectedField.value?.type === 'enum')

/** Whether the value input should be a boolean toggle */
const isBoolean = computed(() =>
    selectedField.value?.type === 'boolean' || selectedField.value?.type === 'virtual-boolean' || props.condition.op === '$exists'
)

/** Whether the value input should be a number */
const isNumber = computed(() => selectedField.value?.type === 'number')

/** Whether the field is a relation */
const isRelation = computed(() => selectedField.value?.type === 'relation')

/** Whether the field is a relation with single-select operator ($eq/$ne) */
const isRelationSingleSelect = computed(() => {
    if (!isRelation.value) return false
    const op = props.condition.op
    return op === '$eq' || op === '$ne'
})

/** Whether the field is a string with $eq operator (use searchable dropdown) */
const isStringEq = computed(() =>
    selectedField.value?.type === 'string' && props.condition.op === '$eq'
)

/** Whether the $in/$nin multi-select should be shown */
const showMultiSelect = computed(() => {
    if (!selectedField.value || isBoolean.value) return false
    const op = props.condition.op
    if (op !== '$in' && op !== '$nin') return false
    return isRelation.value || isEnum.value || selectedField.value.type === 'string'
})

/** Options for multi-select and searchable dropdowns (loaded from API) */
const fieldOptions = ref<MultiSelectOption[]>([])
const isLoadingOptions = ref(false)

/** String field $eq dropdown options */
const stringOptions = ref<string[]>([])
const isLoadingStringOptions = ref(false)
const stringSearchQuery = ref('')

/** Available dynamic tokens for current field */
const applicableTokens = computed(() => {
    if (!selectedField.value) return []
    const type = selectedField.value.type
    return DYNAMIC_TOKENS.filter(t => t.applicableTypes.includes(type as 'string' | 'relation' | 'date'))
})

/** Date tokens specifically */
const dateTokens = computed(() => DYNAMIC_TOKENS.filter(t => t.applicableTypes.includes('date')))

/** Check if a value is a dynamic token (starts with $) */
function isTokenValue(val: unknown): boolean {
    return typeof val === 'string' && val.startsWith('$')
}

/** Load field values from the API (for relation and enum fields) */
async function loadFieldValues(fieldName: string, search?: string) {
    isLoadingOptions.value = true
    try {
        const values = await savedViewService.getFieldValues(props.entityType, fieldName, search)
        let loadedOptions: MultiSelectOption[] = []
        if (Array.isArray(values) && values.length > 0) {
            if (typeof values[0] === 'string') {
                loadedOptions = (values as string[]).map(v => ({ value: v, label: v }))
            } else {
                loadedOptions = (values as { id: string; label: string }[]).map(v => ({
                    value: v.id,
                    label: v.label,
                }))
            }
        }
        // Prepend applicable dynamic tokens (e.g. $CURRENT_USER)
        const tokenOpts: MultiSelectOption[] = applicableTokens.value.map(t => ({
            value: t.token,
            label: `⚡ ${t.label}`,
        }))
        fieldOptions.value = [...tokenOpts, ...loadedOptions]
    } catch {
        const tokenOpts: MultiSelectOption[] = applicableTokens.value.map(t => ({
            value: t.token,
            label: `⚡ ${t.label}`,
        }))
        fieldOptions.value = tokenOpts
    } finally {
        isLoadingOptions.value = false
    }
}

/** Load string field values for $eq searchable dropdown */
async function loadStringOptions(fieldName: string, search?: string) {
    isLoadingStringOptions.value = true
    try {
        const values = await savedViewService.getFieldValues(props.entityType, fieldName, search)
        const loaded = (values as string[]) ?? []
        // Prepend $CURRENT_USER for string fields if applicable
        const tokenValues = applicableTokens.value.map(t => t.token)
        stringOptions.value = [...tokenValues, ...loaded]
    } catch {
        stringOptions.value = applicableTokens.value.map(t => t.token)
    } finally {
        isLoadingStringOptions.value = false
    }
}

/** Debounced search for string $eq fields */
let stringSearchTimeout: ReturnType<typeof setTimeout> | null = null
watch(stringSearchQuery, (query) => {
    if (stringSearchTimeout) clearTimeout(stringSearchTimeout)
    stringSearchTimeout = setTimeout(() => {
        if (selectedField.value && isStringEq.value) {
            loadStringOptions(selectedField.value.name, query || undefined)
        }
    }, 250)
})

// Reset operator and value when field changes
watch(() => props.condition.field, () => {
    const field = selectedField.value
    if (field && field.operators.length > 0) {
        emit('update', { op: field.operators[0], value: '' })
    }
    if (field?.type === 'enum' && field.values) {
        // Enum values are already in the schema — no API call needed
        fieldOptions.value = field.values.map(v => ({ value: v, label: v }))
    } else if (field?.type === 'relation') {
        loadFieldValues(field.name)
    }
    if (field?.type === 'string') {
        loadFieldValues(field.name)
        loadStringOptions(field.name)
    }
})

// Reload options when operator changes (in case switching to/from $eq/$in/$nin)
watch(() => props.condition.op, () => {
    const field = selectedField.value
    if (field?.type === 'string' && props.condition.op === '$eq') {
        loadStringOptions(field.name)
    }
    if (field?.type === 'string' && (props.condition.op === '$in' || props.condition.op === '$nin')) {
        loadFieldValues(field.name)
    }
    if (field?.type === 'enum' && field.values && (props.condition.op === '$in' || props.condition.op === '$nin')) {
        fieldOptions.value = field.values.map(v => ({ value: v, label: v }))
    }
    // Reload relation options for single-select operators ($eq/$ne)
    if (field?.type === 'relation' && (props.condition.op === '$eq' || props.condition.op === '$ne')) {
        loadFieldValues(field.name)
    }
})

function onFieldChange(e: Event) {
    emit('update', { field: (e.target as HTMLSelectElement).value })
}

function onOperatorChange(e: Event) {
    emit('update', { op: (e.target as HTMLSelectElement).value as FilterOperator })
}

function onValueChange(e: Event) {
    const target = e.target as HTMLInputElement | HTMLSelectElement
    let value: unknown = target.value
    if (isNumber.value) value = Number(target.value)
    if (isBoolean.value) value = target.value === 'true'
    emit('update', { value })
}

function onRelationUpdate(ids: string[]) {
    emit('update', { value: ids })
}

function onStringOptionSelect(e: Event) {
    const value = (e.target as HTMLSelectElement).value
    emit('update', { value })
}

// Load options on mount if field is already set
onMounted(() => {
    const field = selectedField.value
    if (field?.type === 'enum' && field.values) {
        fieldOptions.value = field.values.map(v => ({ value: v, label: v }))
    } else if (field?.type === 'relation') {
        loadFieldValues(field.name)
    }
    if (field?.type === 'string') {
        loadFieldValues(field.name)
        loadStringOptions(field.name)
    }
})
function onDateSelectChange(e: Event) {
    const val = (e.target as HTMLSelectElement).value
    if (val === 'custom') {
        emit('update', { value: '' })
    } else {
        emit('update', { value: val })
    }
}
</script>

<template>
    <div class="condition-row">
        <!-- Field Select -->
        <select
            class="condition-row__field"
            :value="condition.field"
            @change="onFieldChange"
        >
            <option value="" disabled>Select field…</option>
            <option
                v-for="f in fields"
                :key="f.name"
                :value="f.name"
            >
                {{ fieldIcon(f.type) }} {{ f.label }}
            </option>
        </select>

        <!-- Operator Select -->
        <select
            class="condition-row__operator"
            :value="condition.op"
            :disabled="!condition.field"
            @change="onOperatorChange"
        >
            <option
                v-for="op in availableOperators"
                :key="op"
                :value="op"
            >
                {{ operatorLabel(op) }}
            </option>
        </select>

        <!-- Value Input (adapts by type) -->

        <!-- $in searchable multi-select (relation or enum) -->
        <MultiSelectDropdown
            v-if="showMultiSelect"
            :model-value="Array.isArray(condition.value) ? condition.value as string[] : []"
            :options="fieldOptions"
            placeholder="Search & select…"
            @update:model-value="onRelationUpdate"
        />

        <!-- String field $eq — searchable dropdown from API -->
        <div v-else-if="isStringEq && stringOptions.length > 0" class="condition-row__string-select-wrap">
            <select
                class="condition-row__value"
                :value="String(condition.value ?? '')"
                @change="onStringOptionSelect"
            >
                <option value="" disabled>Select…</option>
                <option
                    v-for="opt in stringOptions"
                    :key="opt"
                    :value="opt"
                >
                    {{ isTokenValue(opt) ? `⚡ ${opt}` : opt }}
                </option>
            </select>
            <span v-if="isLoadingStringOptions" class="condition-row__loading">…</span>
        </div>

        <!-- Relation field single-select ($eq, $ne) -->
        <select
            v-else-if="isRelationSingleSelect"
            class="condition-row__value"
            :value="String(condition.value ?? '')"
            :disabled="!condition.field || isLoadingOptions"
            @change="onValueChange"
        >
            <option value="" disabled>{{ isLoadingOptions ? 'Loading…' : 'Select…' }}</option>
            <option
                v-for="opt in fieldOptions"
                :key="opt.value"
                :value="opt.value"
            >
                {{ opt.label }}
            </option>
        </select>

        <select
            v-else-if="isEnum && selectedField?.values"
            class="condition-row__value"
            :value="String(condition.value)"
            @change="onValueChange"
        >
            <option value="" disabled>Select…</option>
            <option
                v-for="v in selectedField.values"
                :key="v"
                :value="v"
            >
                {{ v }}
            </option>
        </select>

        <select
            v-else-if="isBoolean"
            class="condition-row__value"
            :value="String(condition.value)"
            @change="onValueChange"
        >
            <option value="true">Yes</option>
            <option value="false">No</option>
        </select>

        <!-- Date Field (Supports static date or dynamic tokens: $TODAY, $YESTERDAY, $WEEK, $LAST_WEEK, $MONTH, $LAST_MONTH, $NOW) -->
        <div v-else-if="isDate" class="condition-row__date-wrap">
            <select
                class="condition-row__value condition-row__date-token-select"
                :value="isTokenValue(condition.value) ? String(condition.value) : 'custom'"
                @change="onDateSelectChange"
            >
                <option value="custom">Specific Date…</option>
                <option
                    v-for="t in dateTokens"
                    :key="t.token"
                    :value="t.token"
                >
                    ⚡ {{ t.label }}
                </option>
            </select>
            <input
                v-if="!isTokenValue(condition.value)"
                class="condition-row__value"
                type="date"
                :value="String(condition.value ?? '')"
                :disabled="!condition.field"
                @change="onValueChange"
            />
        </div>

        <input
            v-else-if="isNumber"
            class="condition-row__value"
            type="number"
            :value="condition.value"
            :disabled="!condition.field"
            placeholder="Value"
            @input="onValueChange"
        />

        <input
            v-else
            class="condition-row__value"
            type="text"
            :value="String(condition.value ?? '')"
            :disabled="!condition.field"
            placeholder="Value"
            @input="onValueChange"
        />

        <!-- Remove Button -->
        <button
            v-if="removable"
            class="condition-row__remove"
            title="Remove condition"
            @click="emit('remove')"
        >
            ×
        </button>
    </div>
</template>

<style scoped>
.condition-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.condition-row__field {
    width: 160px;
    padding: 0.375rem 0.5rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    cursor: pointer;
}

.condition-row__operator {
    width: 120px;
    padding: 0.375rem 0.5rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    cursor: pointer;
}

.condition-row__value {
    flex: 1;
    min-width: 120px;
    padding: 0.375rem 0.5rem;
    font-size: var(--text-sm);
    color: var(--text-primary);
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
}

.condition-row__value:focus {
    border-color: var(--color-primary);
    outline: none;
    box-shadow: 0 0 0 2px rgba(18, 192, 221, 0.15);
}

.condition-row__date-wrap {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex: 1;
}

.condition-row__date-token-select {
    max-width: 160px;
}

.condition-row__remove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    font-size: 1rem;
    color: var(--text-muted);
    background: none;
    border: 1px solid transparent;
    border-radius: var(--radius-full);
    cursor: pointer;
    transition: all 0.15s;
}

.condition-row__remove:hover {
    color: var(--color-danger);
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.2);
}

select option {
    background: var(--bg-surface);
    color: var(--text-primary);
}
</style>
